import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Component, Lock, Unlock, Unlink, Pencil, Wand2, Pin, Play,
  Copy, Trash2, Plus, Palette, RotateCcw, Eraser, Globe, Compass, LayoutGrid,
  ExternalLink, Link2,
} from 'lucide-react';
import { useCanvasStore } from '../store/canvas-store';
import { frameHref, framePermalink } from '../permalink';
import { shortId } from './dropDetect';
import { tidySelection, tidyCanvas, duplicateSelection, deleteSelection } from './selectionActions';
import { usePanelStore } from './panel/panel-store';
import { runFirstRunReplay } from '../templates/firstRunReplay';
import { currentReadOnly } from './readonly';

interface MenuItem {
  label: string;
  icon?: ReactNode;
  /** Right-aligned keyboard hint, e.g. "⌘E". */
  hint?: string;
  /** Destructive action — renders in --color-danger. */
  danger?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  separator?: boolean;
  submenu?: MenuItem[];
}

const ICON = 16;

interface MenuState {
  x: number;
  y: number;
  items: MenuItem[];
}

/**
 * Generic right-click context menu. Listens at window level; routes by
 * target inspection (data-frame-id walked up the tree). Frame menu when
 * over a frame; canvas menu when over empty canvas.
 */
export function ContextMenu() {
  const [state, setState] = useState<MenuState | null>(null);
  const dispatch = useCanvasStore((s) => s.dispatch);
  const beginTxn = useCanvasStore((s) => s.beginTransaction);
  const endTxn = useCanvasStore((s) => s.endTransaction);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onContext = (e: MouseEvent) => {
      // Suppress browser default; show our menu.
      e.preventDefault();
      if (currentReadOnly()) return; // every menu item is an edit — none for viewers
      const frameId = findFrameIdAt(e.target as Element | null);
      if (frameId) {
        const frame = useCanvasStore.getState().doc.frames.find((f) => f.id === frameId);
        if (frame) setState({ x: e.clientX, y: e.clientY, items: frameMenu(frame, dispatch, beginTxn, endTxn) });
      } else {
        setState({ x: e.clientX, y: e.clientY, items: canvasMenu(dispatch) });
      }
    };
    window.addEventListener('contextmenu', onContext);
    return () => window.removeEventListener('contextmenu', onContext);
  }, [dispatch, beginTxn, endTxn]);

  useEffect(() => {
    if (!state) return;
    const opener = document.activeElement as HTMLElement | null;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return;
      setState(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setState(null); opener?.focus?.(); }
    };
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    // Move focus into the menu so Esc/Tab work and a keyboard user lands here.
    containerRef.current?.focus();
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [state]);

  if (!state) return null;

  return (
    <div
      ref={containerRef}
      role="menu"
      tabIndex={-1}
      aria-label="Context menu"
      className="fixed z-[1000] min-w-[208px] p-1 rounded-[6px] font-sans text-[13px] outline-none bg-[var(--color-menu-bg)] border border-[var(--color-menu-border)] text-[var(--color-text)] shadow-[var(--shadow-2)]"
      style={{ left: state.x, top: state.y }}
    >
      {state.items.map((item, i) =>
        item.separator ? (
          <div key={i} className="h-px my-1 bg-[var(--color-border)]" />
        ) : (
          <button
            key={i}
            role="menuitem"
            disabled={item.disabled}
            onClick={() => {
              item.onClick?.();
              setState(null);
            }}
            className={
              'flex items-center gap-2 w-full text-left px-2.5 min-h-[40px] rounded-[4px] border-0 bg-transparent ' +
              'enabled:hover:bg-[var(--color-menu-hover)] disabled:cursor-not-allowed ' +
              (item.disabled
                ? 'text-[var(--color-text-muted)]'
                : item.danger
                  ? 'text-[var(--color-danger)]'
                  : 'text-[var(--color-text)]')
            }
          >
            <span className={'flex w-3.5 shrink-0 ' + (item.danger ? 'opacity-100' : 'opacity-75')}>
              {item.icon}
            </span>
            <span className="flex-1">{item.label}</span>
            {item.hint && <span className="text-[11px] text-[var(--color-text-muted)]">{item.hint}</span>}
          </button>
        ),
      )}
    </div>
  );
}

function findFrameIdAt(el: Element | null): string | null {
  let cur: Element | null = el;
  while (cur) {
    const id = (cur as HTMLElement).getAttribute?.('data-frame-id');
    if (id) return id;
    cur = cur.parentElement;
  }
  return null;
}

type Dispatch = ReturnType<typeof useCanvasStore.getState>['dispatch'];
type Txn = ReturnType<typeof useCanvasStore.getState>['beginTransaction'];

function frameMenu(
  frame: import('../store/types').Frame,
  dispatch: Dispatch,
  beginTxn: Txn,
  endTxn: ReturnType<typeof useCanvasStore.getState>['endTransaction'],
): MenuItem[] {
  const items: MenuItem[] = [];
  // When the right-clicked frame is part of a multi-selection, batch actions
  // (tidy / duplicate / delete) operate on the whole selection.
  const selection = useCanvasStore.getState().selection;
  const multi = selection.length > 1 && selection.includes(frame.id);
  const n = selection.length;
  if (frame.kind === 'free') {
    items.push({ label: 'Make component', icon: <Component size={ICON} />, onClick: () => dispatch({ t: 'frame.promoteToMain', id: frame.id }) });
  } else if (frame.kind === 'main') {
    items.push({
      label: frame.locked ? 'Unlock Main' : 'Lock Main',
      icon: frame.locked ? <Unlock size={ICON} /> : <Lock size={ICON} />,
      onClick: () => dispatch({ t: 'main.setLocked', id: frame.id, locked: !frame.locked }),
    });
    items.push({ label: 'Demote to plain frame', icon: <Unlink size={ICON} />, onClick: () => dispatch({ t: 'frame.demoteFromMain', id: frame.id }) });
  } else if (frame.kind === 'instance') {
    items.push({ label: 'Detach from Main', icon: <Unlink size={ICON} />, onClick: () => dispatch({ t: 'instance.detach', id: frame.id }) });
  } else if (frame.kind === 'tsxFree') {
    items.push({
      label: 'Make component',
      icon: <Component size={ICON} />,
      onClick: () => {
        beginTxn('promote tsx');
        try {
          dispatch({ t: 'tsxFrame.promoteToMain', id: frame.id });
          dispatch({ t: 'tsxInstance.create', id: shortId(), mainId: frame.id, x: frame.x + frame.w + 40, y: frame.y, propValues: {} });
        } finally {
          endTxn();
        }
      },
    });
  } else if (frame.kind === 'tsxMain') {
    items.push({
      label: 'New instance',
      icon: <Plus size={ICON} />,
      onClick: () => {
        dispatch({ t: 'tsxInstance.create', id: shortId(), mainId: frame.id, x: frame.x + frame.w + 40, y: frame.y, propValues: {} });
      },
    });
  }
  if (frame.kind === 'free' || frame.kind === 'main' || frame.kind === 'instance') {
    items.push({ separator: true, label: '' });
    items.push({
      label: 'Edit in panel',
      icon: <Pencil size={ICON} />,
      hint: '⌘E',
      onClick: () => {
        dispatch({ t: 'select', ids: [frame.id] });
        usePanelStore.getState().setOpen(true);
        usePanelStore.getState().setEditorOpen(true);
      },
    });
    items.push({ label: 'Add effect', icon: <Wand2 size={ICON} />, onClick: () => spawnTool('fx', frame.id, dispatch) });
    items.push({
      label: multi ? `Publish ${n} frames…` : 'Publish frame…',
      icon: <Globe size={ICON} />,
      onClick: () => {
        // Publish the current selection if this frame is part of a multi-select,
        // otherwise just this frame.
        if (!multi) dispatch({ t: 'select', ids: [frame.id] });
        usePanelStore.getState().setPublishOpen(true);
      },
    });
  }
  if (frame.kind === 'tsxFree' || frame.kind === 'tsxMain' || frame.kind === 'tsxInstance') {
    items.push({ separator: true, label: '' });
    items.push({
      label: 'Edit in panel',
      icon: <Pencil size={ICON} />,
      hint: '⌘E',
      onClick: () => {
        dispatch({ t: 'select', ids: [frame.id] });
        usePanelStore.getState().setOpen(true);
        usePanelStore.getState().setEditorOpen(true);
      },
    });
  }
  // Design frames get a permalink: preview the frame in isolation in a new tab.
  if (
    frame.kind === 'free' || frame.kind === 'main' || frame.kind === 'instance' ||
    frame.kind === 'tsxFree' || frame.kind === 'tsxMain' || frame.kind === 'tsxInstance'
  ) {
    items.push({
      label: 'Open in new tab',
      icon: <ExternalLink size={ICON} />,
      onClick: () => window.open(frameHref(frame.id), '_blank', 'noopener'),
    });
    items.push({
      label: 'Copy permalink',
      icon: <Link2 size={ICON} />,
      onClick: () => void navigator.clipboard?.writeText(framePermalink(frame.id)),
    });
  }
  if (frame.kind === 'chat' || frame.kind === 'code' || frame.kind === 'fx') {
    if (frame.anchorId) {
      items.push({ label: 'Unanchor (go global)', icon: <Globe size={ICON} />, onClick: () => dispatch({ t: 'toolFrame.setAnchor', id: frame.id, anchorId: null }) });
    }
    if (frame.kind === 'chat' && !frame.pinned) {
      items.push({ label: 'Pin', icon: <Pin size={ICON} />, onClick: () => dispatch({ t: 'toolFrame.pin', id: frame.id }) });
    }
  }
  if (frame.kind === 'note' && frame.tourId) {
    const tourId = frame.tourId;
    items.push({ label: 'Start tour here', icon: <Play size={ICON} />, onClick: () => useCanvasStore.getState().startTour(tourId) });
  }
  items.push({ separator: true, label: '' });
  if (multi) {
    items.push({ label: 'Tidy selection', icon: <LayoutGrid size={ICON} />, onClick: () => tidySelection() });
  }
  items.push({
    label: multi ? `Duplicate ${n} frames` : 'Duplicate',
    icon: <Copy size={ICON} />,
    onClick: () => {
      if (multi) { duplicateSelection(); return; }
      beginTxn('duplicate');
      try {
        const newId = shortId();
        const nx = frame.x + 24, ny = frame.y + 24;
        if (frame.kind === 'tsxFree' || frame.kind === 'tsxMain') {
          dispatch({ t: 'tsxFrame.create', id: newId, x: nx, y: ny, w: frame.w, title: frame.title, tsx: frame.tsx });
        } else if (frame.kind === 'tsxInstance') {
          dispatch({ t: 'tsxInstance.create', id: newId, mainId: frame.mainId, x: nx, y: ny, propValues: { ...frame.propValues }, w: frame.w });
        } else {
          const html = frame.kind === 'free' || frame.kind === 'main' ? frame.html : '';
          dispatch({ t: 'frame.create', id: newId, x: nx, y: ny, w: frame.w, title: frame.title, html });
        }
        // Select the clone so it's ready to move (matches batch-duplicate).
        dispatch({ t: 'select', ids: [newId] });
      } finally {
        endTxn();
      }
    },
  });
  items.push({
    label: multi ? `Delete ${n} frames` : 'Delete',
    icon: <Trash2 size={ICON} />,
    danger: true,
    onClick: () => (multi ? deleteSelection() : dispatch({ t: 'frame.delete', ids: [frame.id] })),
  });
  return items;
}

function spawnTool(kind: 'chat' | 'code' | 'fx', anchorId: string, dispatch: Dispatch): void {
  dispatch({
    t: 'toolFrame.create',
    id: shortId(),
    toolKind: kind,
    x: 100,
    y: 100,
    anchorId,
    pinned: kind === 'chat' ? true : undefined,
  });
}

export function canvasMenu(dispatch: Dispatch): MenuItem[] {
  return [
    {
      label: 'Add styleguide',
      icon: <Palette size={ICON} />,
      onClick: () => dispatch({ t: 'toolFrame.create', id: shortId(), toolKind: 'styleguide', x: 80, y: 80 }),
    },
    {
      label: 'Play intro',
      icon: <Compass size={ICON} />,
      onClick: () => { void runFirstRunReplay({ gate: false }); },
    },
    { separator: true, label: '' },
    {
      label: 'Tidy canvas',
      icon: <LayoutGrid size={ICON} />,
      onClick: () => tidyCanvas(),
    },
    {
      label: 'Reset view',
      icon: <RotateCcw size={ICON} />,
      onClick: () =>
        useCanvasStore.setState((s) => ({
          ...s,
          doc: { ...s.doc, viewport: { pan: { x: 0, y: 0 }, zoom: 1 } },
        })),
    },
    {
      label: 'Clear canvas',
      icon: <Eraser size={ICON} />,
      danger: true,
      onClick: () => {
        const s = useCanvasStore.getState();
        if (s.doc.frames.length > 0 && !window.confirm('Clear the whole canvas? Undo still works.')) return;
        dispatch({ t: 'doc.clear' });
      },
    },
  ];
}
